import { useMemo } from 'react';
import {
  OperationalModelOptions,
  OperationalStatusOptions,
} from '@components/EntityDrawers/constants/common';
import { InsightKeys } from '@constants/entities/insights';
import { useProjectFilters } from '@context/ProjectFilters';
import useProjectData from '@hooks/useProjectData';
import useProjectId from '@hooks/useProjectId';
import { skipToken } from '@reduxjs/toolkit/query';
import { useGetProjectDomainsQuery } from '@store/services/projects';
import { IDomain } from '@store/services/projects/types';
import { CIASettingsCriteriaType } from '@store/services/questionnaire/types';
import { ModelFilterCategory } from '@store/zustand/model-filters';
import {
  SCOPE_FILTER_NO_OPERATIONALISED,
  SCOPE_FILTER_UNASSIGNED,
} from '@utils/domains';
import type {
  CiaFilterCategory,
  FlatModelFilterCategory,
  ModelFilterVocabulary,
} from '@utils/modelFilterCounts';
import {
  CategoryCriteria,
  CategoryOption,
} from '@views/Project/components/ModelFiltersPopover/categories';
import { buildTree, ITreeItem } from '@views/Project/helpers';

/**
 * What the popover needs to render and count every category: the value list
 * of each flat category, the criteria of each CIA category, the labels the
 * root summary reads, which rows are shown at all, and the same values in the
 * shape the counts helper takes.
 */
export type CategoryOptions = {
  options: Partial<Record<FlatModelFilterCategory, CategoryOption[]>>;
  criteria: Partial<Record<CiaFilterCategory, CategoryCriteria[]>>;
  labels: Partial<Record<ModelFilterCategory, Record<string, string>>>;
  availability: Partial<Record<ModelFilterCategory, boolean>>;
  vocabulary: ModelFilterVocabulary;
};

const NO_DOMAINS: IDomain[] = [];
const NO_PARTNERS: string[] = [];

const toOption = ({
  value,
  label,
}: {
  value: string | number;
  label: string;
}): CategoryOption => ({ value: String(value), label });

const identityOptions = (values: string[]): CategoryOption[] =>
  values.map((value) => ({ value, label: value }));

/**
 * The Scope rows: the domain tree flattened depth-first, so a Sub-domain
 * follows its Domain, then the two synthetic rows the retiring Scope modal
 * also offered, under the same names.
 */
const scopeOptions = (domains: IDomain[]): CategoryOption[] => {
  const flatten = (items: ITreeItem[]): CategoryOption[] =>
    items.flatMap(({ id, name, children }) => [
      { value: id, label: name },
      ...flatten(children ?? []),
    ]);

  return [
    ...flatten(buildTree(domains)),
    {
      value: SCOPE_FILTER_NO_OPERATIONALISED,
      label: 'No Operationalised scope',
    },
    { value: SCOPE_FILTER_UNASSIGNED, label: 'Unassigned' },
  ];
};

/**
 * AC3.1.3's "a Domain is configured". The back end seeds every project with
 * the level-0 `Enterprise wide` root whether or not any domain was supplied,
 * so a non-empty list proves nothing; only a domain below the root counts,
 * the same level-0 marker `findWideDomain` and the back end's own root lookup
 * use.
 */
const hasConfiguredDomain = (domains: IDomain[]) =>
  domains.some(({ level }) => level > 0);

const toCriteria = (
  criterias: CIASettingsCriteriaType[] | undefined,
): CategoryCriteria[] =>
  (criterias ?? []).map(({ id, name, answers }) => ({
    id,
    name,
    options: identityOptions(answers),
  }));

const valuesOf = (options: CategoryOption[]) =>
  options.map(({ value }) => value);

const labelsOf = (options: CategoryOption[]) =>
  Object.fromEntries(options.map(({ value, label }) => [value, label]));

const answersByCriteria = (criteria: CategoryCriteria[]) =>
  Object.fromEntries(
    criteria.map(({ id, options }) => [id, valuesOf(options)]),
  );

/**
 * The value vocabulary, display labels, criteria and availability of every
 * category row.
 *
 * The client owns the vocabulary: a value no entity carries still gets a row,
 * reading 0 once counted (AC5.1.1). Validation and Review status come from
 * the filters context, the one place those two lists are defined; Operational
 * status and Operating model are the entity-drawer enums; partners are the
 * project's; Scope reads the same domains query the retiring Scope control
 * read, so it follows domain edits through the `ProjectDomains` tag; and the
 * three CIA categories are the questionnaire's criteria. Availability is
 * AC3.1.1 for partners, AC3.1.3 for Scope (a domain below the seeded root,
 * not merely a non-empty list), and for CIA the gate the retiring
 * `CiaFilters` applied — a questionnaire, the CIA insight in the
 * subscription — plus at least one criteria to list, so no row opens on an
 * empty panel.
 */
export const useCategoryOptions = (): CategoryOptions => {
  const { validationStatusOptions, reviewStatusOptions } = useProjectFilters();
  const [{ partners = NO_PARTNERS, questionnaire, subscription }] =
    useProjectData();
  const projectId = useProjectId();
  const { data: domains = NO_DOMAINS } = useGetProjectDomainsQuery(
    projectId || skipToken,
  );

  return useMemo(() => {
    const options = {
      domains: scopeOptions(domains),
      validationStatus: validationStatusOptions.map(toOption),
      reviewStatus: reviewStatusOptions.map(toOption),
      operationalStatuses: OperationalStatusOptions.map(toOption),
      operatingModelStatuses: OperationalModelOptions.map(toOption),
      partners: identityOptions(partners),
    };

    const hasCia =
      Boolean(questionnaire) &&
      Boolean(subscription?.insights.includes(InsightKeys.CIA));
    const ciaCriteria = hasCia ? questionnaire?.cia_criteria : undefined;

    const criteria = {
      assurance_level: toCriteria(ciaCriteria?.assurance_level.criterias),
      coverage: toCriteria(ciaCriteria?.coverage.criterias),
      applicability: toCriteria(ciaCriteria?.applicability.criterias),
    };

    const labels: CategoryOptions['labels'] = {
      domains: labelsOf(options.domains),
      validationStatus: labelsOf(options.validationStatus),
      reviewStatus: labelsOf(options.reviewStatus),
      operationalStatuses: labelsOf(options.operationalStatuses),
      operatingModelStatuses: labelsOf(options.operatingModelStatuses),
      partners: labelsOf(options.partners),
    };

    const availability: CategoryOptions['availability'] = {
      domains: hasConfiguredDomain(domains),
      partners: partners.length > 0,
      assurance_level: criteria.assurance_level.length > 0,
      coverage: criteria.coverage.length > 0,
      applicability: criteria.applicability.length > 0,
    };

    const vocabulary: ModelFilterVocabulary = {
      domains: valuesOf(options.domains),
      validationStatus: valuesOf(options.validationStatus),
      reviewStatus: valuesOf(options.reviewStatus),
      operationalStatuses: valuesOf(options.operationalStatuses),
      operatingModelStatuses: valuesOf(options.operatingModelStatuses),
      partners: valuesOf(options.partners),
      assurance_level: answersByCriteria(criteria.assurance_level),
      coverage: answersByCriteria(criteria.coverage),
      applicability: answersByCriteria(criteria.applicability),
    };

    return { options, criteria, labels, availability, vocabulary };
  }, [
    domains,
    validationStatusOptions,
    reviewStatusOptions,
    partners,
    questionnaire,
    subscription,
  ]);
};
